import { Navigate } from "react-router-dom";
import { useState } from "react";
import AppLayout from "@/layouts/AppLayout";
import PageHeading from "@/components/PageHeading";
import DetailsCard from "@/components/DetailsCard";
import Switch from "@/components/Switch";
import Button from "@/components/Button";
import usePageTitle from "@/hooks/usePageTitle";
import { useFlags, useFlagsLoaded } from "@/hooks/useFeatureFlags";
import { useColorScheme } from "@/providers/ColorSchemeProvider";
import { clearDefaultTabs, getDefaultTabs } from "@/lib/defaultTab";
import { trackDefaultTabCleared } from "@/lib/defaultTabAmplitude";
import useAmplitude from "@/hooks/useAmplitude";

const DEFAULT_TAB_GROUP_LABELS: Record<string, string> = {
  people: "People",
  home: "Home",
  "people/person": "Person",
  "people/membership": "Membership",
  "people/career": "Career",
  "people/education": "Education"
};

const DEFAULT_TAB_SLUG_LABELS: Record<string, Record<string, string>> = {
  people: {
    index: "All People",
    "membership-services": "Operations"
  },
  "people/person": {
    overview: "Overview",
    contact: "Contact",
    "change-requests": "Change Requests",
    "change-history": "Change History",
    documents: "Documents",
    groups: "Groups",
    timeline: "Timeline",
    notes: "Notes"
  },
  "people/membership": {
    overview: "Overview",
    "path-to-membership": "Path to Membership",
    "prospect-program": "Prospect Program",
    applications: "Applications",
    section: "Section Affiliation",
    committees: "Committees"
  },
  "people/career": {
    overview: "Overview",
    employment: "Employment",
    awards: "Awards",
    "governance-roles": "Governance Roles",
    "career-profile": "Career Profile",
    "job-applications": "Applications",
    "job-preferences": "Preferences",
    compensation: "Compensation",
    "job-postings": "Job Postings"
  },
  "people/education": {
    "professional-development": "Professional Development (PDRs)",
    learning: "Lifelong Learning",
    program: "PGM Program",
    credentials: "Academic Degrees",
    certifications: "Certifications"
  }
};

const EMPTY_DEFAULT_TABS_COPY =
  "No default tabs are saved on this device yet. On a tab bar, select the current tab to save it.";

function defaultTabSlugLabel(group: string, slug: string) {
  return (
    DEFAULT_TAB_SLUG_LABELS[group]?.[slug] ??
    slug
      .split("-")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ")
  );
}

function defaultTabListLabel(group: string, slug: string) {
  const groupLabel = DEFAULT_TAB_GROUP_LABELS[group] ?? group;
  return `${groupLabel}: ${defaultTabSlugLabel(group, slug)}`;
}

export default function SystemPreferencesPage() {
  usePageTitle("/preferences", "System Preferences");
  const flags = useFlags();
  const flagsLoaded = useFlagsLoaded();
  const { colorScheme, setColorScheme } = useColorScheme();
  const { trackEvent } = useAmplitude();
  const [savedTabs, setSavedTabs] = useState(getDefaultTabs);
  const savedTabEntries = Object.entries(savedTabs);

  if (!flagsLoaded) {
    return null;
  }

  if (flags["backoffice-system-preferences"] !== true) {
    return <Navigate to="/" replace />;
  }

  return (
    <AppLayout interior>
      <div className="mx-auto max-w-3xl pb-12" data-testid="system-preferences-page">
        <PageHeading
          title="System Preferences"
          containerClassName="pt-6 lg:pt-8"
          titleClassName="text-sys-brand-on-surface text-headline-medium-bold font-serif lg:text-headline-large-bold"
        />
        <div className="mt-8 flex flex-col gap-6">
          <DetailsCard heading="Appearance">
            <Switch
              headline="Dark Mode"
              supportingText="Use a darker color scheme in Member 360. This device remembers your choice."
              selected={colorScheme === "dark"}
              onChange={() => setColorScheme(colorScheme === "dark" ? "light" : "dark")}
            />
          </DetailsCard>
          {flags["backoffice-navigation-preferences"] === true && (
            <DetailsCard heading="Navigation">
              <div className="flex flex-col gap-6">
                <p className="text-body-medium text-sys-brand-on-surface-variant">
                  {savedTabEntries.length > 0 ? "Default tabs saved on this device:" : EMPTY_DEFAULT_TABS_COPY}
                </p>
                {savedTabEntries.length > 0 && (
                  <ul className="list-disc pl-5 text-body-medium text-sys-brand-on-surface">
                    {savedTabEntries.map(([group, slug]) => (
                      <li key={group}>{defaultTabListLabel(group, slug)}</li>
                    ))}
                  </ul>
                )}
                {savedTabEntries.length > 0 && (
                  <div>
                    <Button
                      variant="text"
                      onClick={() => {
                        const navigationGroups = Object.keys(savedTabs);
                        clearDefaultTabs();
                        if (Object.keys(getDefaultTabs()).length === 0) {
                          trackDefaultTabCleared(trackEvent, navigationGroups);
                          setSavedTabs({});
                        }
                      }}
                    >
                      Clear default tabs
                    </Button>
                  </div>
                )}
              </div>
            </DetailsCard>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
